import { useState, useCallback, useRef, Fragment, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from '@/components/ui/select';
import {
 Table,
 TableBody,
 TableCell,
 TableHead,
 TableHeader,
 TableRow,
} from '@/components/ui/table';
import {
 DropdownMenu,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
 AlertDialog,
 AlertDialogAction,
 AlertDialogCancel,
 AlertDialogContent,
 AlertDialogDescription,
 AlertDialogFooter,
 AlertDialogHeader,
 AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Shirt, Trash2, Image as ImageIcon, Plus, StickyNote, MoreVertical, Copy, Info } from 'lucide-react';
import { toast } from 'sonner';
import PatternSelectorModal from '@/components/garments/PatternSelectorModal';
import { getGarmentDisplayNo, extractGarmentSuffix } from '@/lib/displayUtils';
import { isTestGarment } from '@/lib/orderItemFilters';
import { formatDateWord } from '@/lib/dateFormat';
import DateField from '@/components/common/DateField';

// Helper to find matching line item for a garment
const findMatchingLineItem = (garment, lineItemsWithRefs, matchedLineItems) => {
 // First try to match by line_item_id
 let lineItem = lineItemsWithRefs.find(li => li.line_item_id === garment.line_item_id);
 
 // If no match, try to match by garment_type and product name (for legacy data)
 if (!lineItem) {
 lineItem = lineItemsWithRefs.find(li => {
 const garmentType = garment.garment_type?.toLowerCase();
 const productName = li.product_name?.toLowerCase();
 const liGarmentType = li.garment_type?.toLowerCase();
 const alreadyMatched = matchedLineItems && matchedLineItems.has(li.line_item_id);
 return !alreadyMatched && (garmentType === productName || garmentType === liGarmentType);
 });
 }
 
 return lineItem;
};

const garmentStatusOptions = [
 { value: 'pending', label: 'Pending', color: 'bg-slate-500/20 text-slate-600 border-slate-500/30' },
 { value: 'not_started', label: 'Not Started', color: 'bg-slate-500/20 text-slate-600 border-slate-500/30' },
 { value: 'under_cutting', label: 'Under Cutting', color: 'bg-blue-500/20 text-blue-600 border-blue-500/30' },
 { value: 'under_stitching', label: 'Under Stitching', color: 'bg-purple-500/20 text-purple-600 border-purple-500/30' },
 { value: 'under_finishing', label: 'Under Finishing', color: 'bg-cyan-500/20 text-cyan-600 border-cyan-500/30' },
 { value: 'ready_dispatch', label: 'Ready for Dispatch', color: 'bg-orange-500/20 text-orange-600 border-amber-500/30' },
 { value: 'dispatched', label: 'Dispatched', color: 'bg-green-500/20 text-green-600 border-green-500/30' },
];

const priorityOptions = [
 { value: 'low', label: 'Low' },
 { value: 'medium', label: 'Medium' },
 { value: 'normal', label: 'Normal' },
 { value: 'high', label: 'High' },
 { value: 'urgent', label: 'Urgent' },
];

const getStatusConfig = (status) => {
 return garmentStatusOptions.find(s => s.value === status) || { label: status, color: 'bg-gray-500/20 text-gray-600' };
};

const GarmentSubForm = ({ garments, fabricOrders = [], lineItemsWithRefs, customerId, factories = [], stores = [], onStatusChange, onUpdate, onDelete, onOpenImages, onAddGarment, onRefresh, products, onRemakeGarment }) => {
 // Track all pending changes per garment
 const [pendingChanges, setPendingChanges] = useState({});
 // Mirrors `pendingChanges` synchronously so save calls scheduled via setTimeout(0)
 // (e.g. after a Select/date field change) always read the LATEST edits instead of a
 // stale closure captured before the just-applied change was committed to state.
 const pendingChangesRef = useRef({});
 
 // Delete confirmation dialog state
 const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
 const [garmentToDelete, setGarmentToDelete] = useState(null);
 
 // Pattern selector modal state
 const [patternSelectorOpen, setPatternSelectorOpen] = useState(false);
 const [selectedGarmentForPattern, setSelectedGarmentForPattern] = useState(null);
 const [selectedGarmentIds, setSelectedGarmentIds] = useState([]);
 const [bulkFactorySelection, setBulkFactorySelection] = useState('__none__');
 const [bulkIdd, setBulkIdd] = useState('');
 const [bulkCdd, setBulkCdd] = useState('');
 const [bulkTdd, setBulkTdd] = useState('');
 const [bulkAssigningFactory, setBulkAssigningFactory] = useState(false);
 const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
 
 // Helper function to get fabric SKUs for a garment
 const getFabricInfo = (garmentId) => {
  const garmentFabrics = fabricOrders?.filter(fo => fo.garment_id === garmentId) || [];
  const mainFabric = garmentFabrics.find(fo => fo.fabric_type === 'main_fabric');
  const lining = garmentFabrics.find(fo => fo.fabric_type === 'lining');
  return {
   mainFabricSku: mainFabric?.fabric_sku || '—',
   liningSku: lining?.fabric_sku || '—'
  };
 };

 // Handle field change (track locally, don't save yet)
 // The ref is mutated synchronously (before setPendingChanges) so that any save
 // triggered right after - even in the same tick, without waiting for a re-render -
 // is guaranteed to see this latest edit instead of a stale pre-edit snapshot.
 const handleFieldChange = useCallback((garmentId, field, value) => {
 const next = {
 ...pendingChangesRef.current,
 [garmentId]: {
 ...(pendingChangesRef.current[garmentId] || {}),
 [field]: value
 }
 };
 pendingChangesRef.current = next;
 setPendingChanges(next);
 }, []);

 // Get current value (pending change or original)
 const getCurrentValue = (garment, field) => {
 if (pendingChanges[garment.garment_id]?.[field] !== undefined) {
 return pendingChanges[garment.garment_id][field];
 }
 return garment[field];
 };

 const saveGarmentChanges = useCallback(async (garmentId) => {
 const changes = pendingChangesRef.current[garmentId];
 if (!changes || Object.keys(changes).length === 0) return;

 try {
 if (changes.garment_status) {
 await onStatusChange(garmentId, changes.garment_status);
 }

 const otherChanges = { ...changes };
 delete otherChanges.garment_status;
 if (Object.keys(otherChanges).length > 0 && onUpdate) {
 await onUpdate(garmentId, otherChanges);
 }

 setPendingChanges(prev => ({
 ...prev,
 [garmentId]: {},
 }));
 pendingChangesRef.current = { ...pendingChangesRef.current, [garmentId]: {} };
 } catch (error) {
 toast.error('Failed to auto-save garment changes');
 }
 }, [onStatusChange, onUpdate]);

 const selectedGarmentsCount = selectedGarmentIds.length;
 const allSelected = garments?.length > 0 && selectedGarmentsCount === garments.length;

 const selectedFactoryLabel = useMemo(() => {
  if (bulkFactorySelection === '__none__') return 'No factory';
  const selected = factories.find((f) => f.factory_id === bulkFactorySelection);
  return selected ? `${selected.factory_name} (${selected.factory_code})` : bulkFactorySelection;
 }, [bulkFactorySelection, factories]);

 const handleToggleSelectAll = (checked) => {
  if (checked) {
   setSelectedGarmentIds((garments || []).map((g) => g.garment_id));
   return;
  }
  setSelectedGarmentIds([]);
 };

 const handleToggleRowSelection = (garmentId, checked) => {
  setSelectedGarmentIds((prev) => {
   if (checked) {
    if (prev.includes(garmentId)) return prev;
    return [...prev, garmentId];
   }
   return prev.filter((id) => id !== garmentId);
  });
 };

 const handleBulkAssignFactory = async () => {
  if (!selectedGarmentIds.length) {
   toast.error('Select at least one garment');
   return;
  }

  setBulkAssigningFactory(true);
  let successCount = 0;
  let failureCount = 0;

  try {
   for (const garmentId of selectedGarmentIds) {
    try {
     await onUpdate?.(garmentId, {
      production_factory_id: bulkFactorySelection === '__none__' ? null : bulkFactorySelection,
     });
     successCount += 1;
    } catch {
     failureCount += 1;
    }
   }

   if (successCount > 0) {
    toast.success(`Factory applied to ${successCount} garment(s): ${selectedFactoryLabel}`);
   }
   if (failureCount > 0) {
    toast.error(`Failed to update ${failureCount} garment(s)`);
   }

   if (successCount > 0) {
    setSelectedGarmentIds([]);
    if (onRefresh) {
     await onRefresh();
    }
   }
  } finally {
   setBulkAssigningFactory(false);
  }
 };

 const handleBulkApplyFields = async () => {
  if (!selectedGarmentIds.length) {
   toast.error('Select at least one garment');
   return;
  }

  const hasFactory = bulkFactorySelection !== '__none__';
  const hasIdd = Boolean(bulkIdd);
  const hasCdd = Boolean(bulkCdd);
  const hasTdd = Boolean(bulkTdd);

  if (!hasFactory && !hasIdd && !hasCdd && !hasTdd) {
   toast.error('Select at least one bulk value to apply');
   return;
  }

  setBulkAssigningFactory(true);
  let successCount = 0;
  let failureCount = 0;

  try {
   for (const garmentId of selectedGarmentIds) {
    const payload = {};
    if (hasFactory) payload.production_factory_id = bulkFactorySelection;
    if (hasIdd) payload.internal_delivery_date = bulkIdd;
    if (hasCdd) payload.client_final_date = bulkCdd;
    if (hasTdd) payload.planned_dispatch_date = bulkTdd;

    try {
     await onUpdate?.(garmentId, payload);
     successCount += 1;
    } catch {
     failureCount += 1;
    }
   }

   if (successCount > 0) {
    toast.success(`Bulk update applied to ${successCount} garment(s)`);
   }
   if (failureCount > 0) {
    toast.error(`Failed to update ${failureCount} garment(s)`);
   }

   if (successCount > 0 && onRefresh) {
    await onRefresh();
   }
  } finally {
   setBulkAssigningFactory(false);
  }
 };

 const handleBulkDeleteSelected = async () => {
  if (!selectedGarmentIds.length) {
   setBulkDeleteConfirmOpen(false);
   return;
  }

  setBulkAssigningFactory(true);
  let successCount = 0;
  let failureCount = 0;

  try {
   for (const garmentId of selectedGarmentIds) {
    try {
     await onDelete?.(garmentId);
     successCount += 1;
    } catch {
     failureCount += 1;
    }
   }

   if (successCount > 0) {
    toast.success(`Deleted ${successCount} garment(s)`);
   }
   if (failureCount > 0) {
    toast.error(`Failed to delete ${failureCount} garment(s)`);
   }

   setSelectedGarmentIds([]);
   if (successCount > 0 && onRefresh) {
    await onRefresh();
   }
  } finally {
   setBulkAssigningFactory(false);
   setBulkDeleteConfirmOpen(false);
  }
 };
 
 // Handle delete with confirmation
 const handleDeleteClick = (garmentId, garmentNo) => {
 setGarmentToDelete({ garmentId, garmentNo });
 setDeleteConfirmOpen(true);
 };
 
 const handleConfirmDelete = async () => {
 if (!garmentToDelete) return;
 
 try {
 await onDelete(garmentToDelete.garmentId);
 toast.success('Garment deleted');
 } catch (error) {
 toast.error('Failed to delete garment');
 } finally {
 setDeleteConfirmOpen(false);
 setGarmentToDelete(null);
 }
 };
 
 // Handle pattern selector
 const handleOpenPatternSelector = (garment) => {
  const linkedLineItem = findMatchingLineItem(garment, lineItemsWithRefs);
  setSelectedGarmentForPattern({
    ...garment,
    is_test_garment: isTestGarment(garment, linkedLineItem),
  });
  setPatternSelectorOpen(true);
 };
 
 const handleSelectPattern = async (patternCode) => {
  if (!selectedGarmentForPattern) return false;

  try {
   if (onUpdate) {
    await onUpdate(selectedGarmentForPattern.garment_id, { pattern_no: patternCode });
   } else {
    handleFieldChange(selectedGarmentForPattern.garment_id, 'pattern_no', patternCode);
    await saveGarmentChanges(selectedGarmentForPattern.garment_id);
   }

   setPendingChanges(prev => ({
    ...prev,
    [selectedGarmentForPattern.garment_id]: {
     ...(prev[selectedGarmentForPattern.garment_id] || {}),
     pattern_no: patternCode,
    },
   }));
   toast.success(`Pattern ${patternCode} saved`);
   return true;
  } catch (error) {
   toast.error('Failed to save pattern no');
   return false;
  }
 };

 // Build a map to track which line items have been matched to garments
 const matchedLineItems = new Set();

 return (
 <div className="w-full min-w-0">
 {/* Header */}
 <div className="p-4 border-b border-slate-200 bg-white flex items-center justify-between">
 <p className="text-sm font-medium text-slate-700">
 {garments?.length || 0} garment(s)
 </p>
 <div className="flex items-center gap-2">
 {selectedGarmentsCount > 0 && (
  <div className="flex flex-wrap items-end gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5" data-testid="edit-order-garments-bulk-factory-bar">
   <span className="text-xs text-slate-600" data-testid="edit-order-garments-bulk-selected-count">{selectedGarmentsCount} selected</span>
   <div className="flex flex-col gap-1" data-testid="edit-order-garments-bulk-factory-field-group">
    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Factory</span>
    <Select value={bulkFactorySelection} onValueChange={setBulkFactorySelection}>
     <SelectTrigger className="h-8 min-w-[220px] text-xs" data-testid="edit-order-garments-bulk-factory-select-trigger">
      <SelectValue placeholder="Select factory" />
     </SelectTrigger>
     <SelectContent data-testid="edit-order-garments-bulk-factory-select-content">
      <SelectItem value="__none__" data-testid="edit-order-garments-bulk-factory-option-none">No factory</SelectItem>
      {factories.map((factory) => (
       <SelectItem
        key={factory.factory_id}
        value={factory.factory_id}
        data-testid={`edit-order-garments-bulk-factory-option-${factory.factory_id}`}
       >
        {factory.factory_name} ({factory.factory_code})
       </SelectItem>
      ))}
     </SelectContent>
    </Select>
   </div>
   <div className="flex flex-col gap-1" data-testid="edit-order-garments-bulk-idd-field-group">
    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">IDD</span>
    <DateField
     value={bulkIdd}
     onChange={(next) => setBulkIdd(next)}
     className="h-8 w-[140px] text-xs"
     testId="edit-order-garments-bulk-idd-input"
     allowClear={false}
    />
   </div>
   <div className="flex flex-col gap-1" data-testid="edit-order-garments-bulk-cdd-field-group">
    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">CDD</span>
    <DateField
     value={bulkCdd}
     onChange={(next) => setBulkCdd(next)}
     className="h-8 w-[140px] text-xs"
     testId="edit-order-garments-bulk-cdd-input"
     allowClear={false}
    />
   </div>
   <div className="flex flex-col gap-1" data-testid="edit-order-garments-bulk-tdd-field-group">
    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Planned Dispatch</span>
    <DateField
     value={bulkTdd}
     onChange={(next) => setBulkTdd(next)}
     className="h-8 w-[140px] text-xs"
     testId="edit-order-garments-bulk-tdd-input"
     allowClear={false}
    />
   </div>
   <Button
    size="sm"
    onClick={handleBulkApplyFields}
    disabled={bulkAssigningFactory}
    data-testid="edit-order-garments-bulk-apply-button"
   >
    {bulkAssigningFactory ? 'Applying...' : 'Apply Selected'}
   </Button>
   <Button
    size="sm"
    variant="destructive"
    onClick={() => setBulkDeleteConfirmOpen(true)}
    disabled={bulkAssigningFactory}
    data-testid="edit-order-garments-bulk-delete-button"
   >
    Delete Selected
   </Button>
  </div>
 )}
 {onAddGarment && (
<Button size="sm" onClick={onAddGarment} data-testid="edit-order-garments-add-garment-button">
 <Plus className="w-4 h-4 mr-1" />
 Add Garment
 </Button>
 )}
 </div>
 </div>

 <ScrollArea className="w-full" data-testid="edit-order-garments-horizontal-scroll-area">
 <Table className="table-fixed min-w-[1100px]" data-testid="edit-order-garments-table">
 <TableHeader>
 <TableRow className="bg-slate-50 border-b border-slate-200">
 <TableHead className="w-[44px] text-slate-700 font-semibold text-xs" data-testid="edit-order-garments-select-all-head">
  <Checkbox
   checked={allSelected}
   onCheckedChange={handleToggleSelectAll}
   data-testid="edit-order-garments-select-all-checkbox"
  />
 </TableHead>
 <TableHead className="w-[220px] text-slate-700 font-semibold text-xs">Garment No</TableHead>
 <TableHead className="w-[110px] text-slate-700 font-semibold text-xs">Main Fabric</TableHead>
 <TableHead className="w-[110px] text-slate-700 font-semibold text-xs">Lining</TableHead>
 <TableHead className="w-[100px] text-slate-700 font-semibold text-xs">Priority</TableHead>
 <TableHead className="w-[180px] text-slate-700 font-semibold text-xs">Pattern No</TableHead>
 <TableHead className="w-[155px] text-slate-700 font-semibold text-xs">Dispatch Date</TableHead>
 <TableHead className="w-[140px] text-slate-700 font-semibold text-xs">Status</TableHead>
 <TableHead className="w-[100px] text-right text-slate-700 font-semibold text-xs">Actions</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
{(!garments || garments.length === 0) ? (
 <TableRow>
<TableCell colSpan={9} className="text-center py-8 text-slate-500" data-testid="edit-order-garments-empty-state">
 <Shirt className="w-10 h-10 mx-auto mb-2 opacity-50" />
 <p>No garments yet</p>
 </TableCell>
 </TableRow>
 ) : (
 garments.map((garment) => {
 const lineItem = findMatchingLineItem(garment, lineItemsWithRefs, matchedLineItems);
 if (lineItem) {
 matchedLineItems.add(lineItem.line_item_id);
 }
 
 const hasChanges = pendingChanges[garment.garment_id] && Object.keys(pendingChanges[garment.garment_id]).length > 0;
 const statusConfig = getStatusConfig(getCurrentValue(garment, 'garment_status') || 'not_started');
 const currentNotes = getCurrentValue(garment, 'notes') || '';
 
 // Get display number - prefer extracting from stored garment_no, fallback to calculation
 const displayNo = garment.garment_no 
   ? extractGarmentSuffix(garment.garment_no, garment.order_number)
   : getGarmentDisplayNo(garment, garments, lineItemsWithRefs);
 
 return (
 <Fragment key={garment.garment_id}>
 {/* Main Garment Row */}
 <TableRow 
 className={`hover:bg-slate-50 border-b-0 ${hasChanges ? 'bg-orange-50/50' : 'bg-white'}`}
 >
 <TableCell>
 <Checkbox
 checked={selectedGarmentIds.includes(garment.garment_id)}
 onCheckedChange={(checked) => handleToggleRowSelection(garment.garment_id, !!checked)}
 data-testid={`edit-order-garments-row-select-checkbox-${garment.garment_id}`}
 />
 </TableCell>
 <TableCell className="pr-1 align-top">
 <div className="space-y-0.5 leading-tight" data-testid={`edit-order-garment-meta-cell-${garment.garment_id}`}>
  <Badge variant="outline" className="font-mono text-xs font-semibold bg-orange-50 text-orange-700 border-orange-300">
   {displayNo}
  </Badge>
  <div className="text-[10px] text-slate-600" data-testid={`edit-order-garment-factory-inline-${garment.garment_id}`}>
   <span className="font-medium">Factory:</span>{' '}
   {(() => {
    const factoryId = getCurrentValue(garment, 'production_factory_id');
    const selectedFactory = factories.find((f) => f.factory_id === factoryId);
    return selectedFactory ? selectedFactory.factory_name : 'No factory';
   })()}
  </div>
  <div className="text-[10px] text-slate-600" data-testid={`edit-order-garment-idd-inline-${garment.garment_id}`}>
   <span className="font-medium">IDD:</span>{' '}
   {formatDateWord(getCurrentValue(garment, 'internal_delivery_date'))}
  </div>
  <div className="text-[10px] text-slate-600" data-testid={`edit-order-garment-cdd-inline-${garment.garment_id}`}>
   <span className="font-medium">CDD:</span>{' '}
   {formatDateWord(getCurrentValue(garment, 'client_final_date'))}
  </div>
 </div>
 </TableCell>
 <TableCell>
 <span className="text-xs text-slate-600">
 {getFabricInfo(garment.garment_id).mainFabricSku}
 </span>
 </TableCell>
 <TableCell>
 <span className="text-xs text-slate-600">
 {getFabricInfo(garment.garment_id).liningSku}
 </span>
 </TableCell>
 {/* Priority - Editable Select */}
 <TableCell>
 <Select
 value={getCurrentValue(garment, 'priority') || 'normal'}
onValueChange={(v) => {
handleFieldChange(garment.garment_id, 'priority', v);
setTimeout(() => saveGarmentChanges(garment.garment_id), 0);
}}
 >
 <SelectTrigger className="h-7 text-xs border-dashed" data-testid={`edit-order-garment-priority-trigger-${garment.garment_id}`}>
 <SelectValue />
 </SelectTrigger>
 <SelectContent data-testid={`edit-order-garment-priority-content-${garment.garment_id}`}>
 {priorityOptions.map(opt => (
 <SelectItem key={opt.value} value={opt.value} data-testid={`edit-order-garment-priority-option-${garment.garment_id}-${opt.value}`}>{opt.label}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </TableCell>
 {/* Pattern No - Editable Input with Info Icon */}
 <TableCell>
 <div className="flex items-center gap-1">
  <Input
   value={getCurrentValue(garment, 'pattern_no') || ''}
   onChange={(e) => handleFieldChange(garment.garment_id, 'pattern_no', e.target.value)}
   onBlur={() => saveGarmentChanges(garment.garment_id)}
   placeholder="Select..."
   className="h-7 text-xs text-slate-700 bg-white border-dashed"
  />
  <Button
   variant="ghost"
   size="icon"
   className="h-7 w-7 flex-shrink-0"
   onClick={() => handleOpenPatternSelector(garment)}
   title="Select from available patterns"
   data-testid={`open-pattern-selector-${garment.garment_id}`}
  >
   <Info className="w-4 h-4 text-blue-500" />
  </Button>
 </div>
 </TableCell>
{/* Dispatch Date - Editable Date Input */}
 <TableCell>
 <DateField
 value={getCurrentValue(garment, 'planned_dispatch_date')?.split('T')[0] || ''}
 onChange={(next) => {
 handleFieldChange(garment.garment_id, 'planned_dispatch_date', next);
 setTimeout(() => saveGarmentChanges(garment.garment_id), 0);
 }}
 className="h-7 min-w-[145px] w-full text-xs text-slate-700 bg-white border-dashed"
 testId={`edit-order-garment-dispatch-date-input-${garment.garment_id}`}
 allowClear={true}
 />
 </TableCell>
 <TableCell>
 <Select
 value={getCurrentValue(garment, 'garment_status') || 'not_started'}
onValueChange={(v) => {
handleFieldChange(garment.garment_id, 'garment_status', v);
setTimeout(() => saveGarmentChanges(garment.garment_id), 0);
}}
 >
 <SelectTrigger className="h-7 text-xs border-0 bg-transparent hover:bg-secondary/50 p-0">
 <Badge variant="outline" className={`${statusConfig.color} text-xs`}>
 {statusConfig.label}
 </Badge>
 </SelectTrigger>
 <SelectContent>
 {garmentStatusOptions.map(opt => (
 <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </TableCell>
 <TableCell className="text-right">
 <div className="flex items-center justify-end gap-1">
 <Button
 variant="ghost"
 size="icon"
 className="h-8 w-8 relative"
 onClick={() => onOpenImages && onOpenImages(garment)}
 title="Manage images"
 >
 <ImageIcon className="w-4 h-4" />
 {garment.image_count > 0 && (
 <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
 {garment.image_count}
 </span>
 )}
 </Button>
 
 {/* 3-Dot Menu for Delete */}
 <DropdownMenu>
 <DropdownMenuTrigger asChild>
 <Button
 variant="ghost"
 size="icon"
 className="h-8 w-8 text-slate-500 hover:text-slate-700"
 >
 <MoreVertical className="w-4 h-4" />
 </Button>
 </DropdownMenuTrigger>
 <DropdownMenuContent align="end">
 <DropdownMenuItem
 className="cursor-pointer"
 onClick={() => {
 if (onRemakeGarment) {
 onRemakeGarment(garment);
 }
 }}
data-testid={`edit-order-garment-remake-button-${garment.garment_id}`}
 >
 <Copy className="w-4 h-4 mr-2" />
 Remake Garment
 </DropdownMenuItem>
 <DropdownMenuItem
 className="text-red-600 focus:text-red-600 cursor-pointer"
 onClick={() => handleDeleteClick(garment.garment_id, garment.garment_no)}
 >
 <Trash2 className="w-4 h-4 mr-2" />
 Delete Garment
 </DropdownMenuItem>
 </DropdownMenuContent>
 </DropdownMenu>
 </div>
 </TableCell>
 </TableRow>
 
 {/* Notes Row - Always visible */}
 <TableRow className={`border-b ${hasChanges ? 'bg-orange-50/50' : 'bg-slate-50/50'}`}>
 <TableCell colSpan={9} className="py-1.5 px-4">
 <div className="flex items-center gap-2">
 <StickyNote className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
 <Input
 value={currentNotes}
 onChange={(e) => handleFieldChange(garment.garment_id, 'notes', e.target.value)}
onBlur={() => saveGarmentChanges(garment.garment_id)}
 placeholder="Add notes..."
 className="h-7 text-xs text-slate-700 bg-white border-dashed"
 />
 </div>
 </TableCell>
 </TableRow>
 </Fragment>
 );
 })
 )}
 </TableBody>
 </Table>
 <ScrollBar orientation="horizontal" />
 </ScrollArea>
 
 {/* Delete Confirmation Dialog */}
 <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
  <AlertDialogContent>
   <AlertDialogHeader>
    <AlertDialogTitle>Delete Garment</AlertDialogTitle>
    <AlertDialogDescription>
     Are you sure you want to delete garment <strong>{garmentToDelete?.garmentNo}</strong>? This action cannot be undone and will also delete associated fabric orders.
    </AlertDialogDescription>
   </AlertDialogHeader>
   <AlertDialogFooter>
    <AlertDialogCancel>Cancel</AlertDialogCancel>
    <AlertDialogAction 
     onClick={handleConfirmDelete}
     className="bg-red-600 hover:bg-red-700"
    >
     Delete
    </AlertDialogAction>
   </AlertDialogFooter>
  </AlertDialogContent>
 </AlertDialog>

 {/* Bulk Delete Confirmation Dialog */}
 <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
  <AlertDialogContent>
   <AlertDialogHeader>
    <AlertDialogTitle>Delete Selected Garments</AlertDialogTitle>
    <AlertDialogDescription>
     Are you sure you want to delete <strong>{selectedGarmentsCount}</strong> selected garment(s)? This action cannot be undone and will delete associated fabric orders.
    </AlertDialogDescription>
   </AlertDialogHeader>
   <AlertDialogFooter>
    <AlertDialogCancel>Cancel</AlertDialogCancel>
    <AlertDialogAction
     onClick={handleBulkDeleteSelected}
     className="bg-red-600 hover:bg-red-700"
     data-testid="edit-order-garments-bulk-delete-confirm-button"
    >
     Delete Selected
    </AlertDialogAction>
   </AlertDialogFooter>
  </AlertDialogContent>
 </AlertDialog>
 
 {/* Pattern Selector Modal */}
 {selectedGarmentForPattern && customerId && (
  (() => {
    const sourceLineItemForPattern = findMatchingLineItem(selectedGarmentForPattern, lineItemsWithRefs, null);
    return (
  <PatternSelectorModal
   isOpen={patternSelectorOpen}
   onClose={() => {
    setPatternSelectorOpen(false);
    setSelectedGarmentForPattern(null);
   }}
   customerId={customerId}
   garmentType={selectedGarmentForPattern.garment_type}
   garment={selectedGarmentForPattern}
   sourceCustomizations={sourceLineItemForPattern?.customizations || selectedGarmentForPattern?.customizations || []}
   availableStores={(stores || []).map((store) => store.name || store.store_id || store).filter(Boolean)}
   onSelectPattern={handleSelectPattern}
  />
    );
  })()
 )}
</div>
 );
};

export default GarmentSubForm;
export { findMatchingLineItem };
